import { type QueryClient, useQuery } from "@tanstack/react-query";

import {
  getBrowseLocationId,
  isAnonymousUser,
  useBrowseLocationId,
} from "@/modules/anonymousBrowsing";
import { getPlatformLocation } from "api/methods";
import type { Models } from "api/types";

type CatalogUser = Pick<Models<"UserModel1">, "id" | "platformLocation">;

/**
 * A visitor's location as `BrowseLocationResolver` settles it: the browse location they picked while it is
 * still one of the practice's active locations, else the practice default. `GET /platform-location` answers
 * with exactly that because the pick rides on the browse-location header; keying by the pick re-asks on a switch.
 */
const browseLocationQuery = (browseLocationId: string | null) => ({
  queryKey: ["catalog/browse-location", browseLocationId],
  queryFn: async () => (await getPlatformLocation()).id,
  staleTime: Infinity,
});

/** The location every catalog query is scoped to: the patient's own, or the visitor's browse location. */
export const fetchCatalogLocationId = async (
  queryClient: QueryClient,
  user: CatalogUser,
): Promise<string | undefined> =>
  isAnonymousUser(user)
    ? queryClient.fetchQuery(browseLocationQuery(getBrowseLocationId()))
    : user.platformLocation?.id;

/**
 * `fetchCatalogLocationId` for a component; `isReady` stays false until a visitor's location is known, and `error`
 * says why it never will be.
 */
export const useCatalogLocationId = (user: CatalogUser | null | undefined) => {
  const browseLocationId = useBrowseLocationId();
  const isAnonymous = isAnonymousUser(user);
  const { data: browseLocation, error } = useQuery({
    ...browseLocationQuery(browseLocationId),
    enabled: isAnonymous,
  });

  if (!user) return { platformLocationId: undefined, isReady: false, error: null };
  return isAnonymous
    ? { platformLocationId: browseLocation, isReady: browseLocation !== undefined, error }
    : { platformLocationId: user.platformLocation?.id, isReady: true, error: null };
};
